<template>
  <div
    v-if="invoice"
    :class="['table-cont', className, { 'tech-app': techMode }]"
  >
    <InvoiceHeader v-bind="$props" />
    <div class="invoice-body">
      <SpbPastDueBanner :invoice="invoice" />
      <LineItemTable
        :data="invoice"
        type="invoice"
        :equipment-types-enabled="equipmentTypesEnabled"
      />
      <Totals :data="invoice" type="invoice" :tech-mode="techMode" />
      <slot name="paymentSchedule" />
      <hr />
      <Signatures
        v-if="invoice.signature || (invoice.job_id && !invoice.voided)"
        :data="invoice.signature"
        :event-id="invoice.invoice_id"
        placeholder="Work Completed"
        approval-text="Work Completed"
        type="invoice"
        :tech-mode="techMode"
      />
      <div v-else-if="invoice.finalized" class="tw-text-right tw-mt-6">
        <strong>Invoice Date:</strong>
        {{ formatDate(invoice.invoice_date, { timezone: tenantTimezone }) }}
      </div>
      <TransactionDetails
        :transactions="invoice.payments"
        :tech-mode="techMode"
      />
      <InvoiceNotes :notes="invoice.notes" />
      <InvoiceFooter :customizations="customizations" />
    </div>
  </div>
</template>

<script lang="ts">
import InvoiceFooter from "serviceshift-ui/components/QuoteInvoice/InvoiceFooter.vue";
import InvoiceHeader from "serviceshift-ui/components/QuoteInvoice/InvoiceHeader.vue";
import InvoiceNotes from "serviceshift-ui/components/QuoteInvoice/InvoiceNotes.vue";
import LineItemTable from "serviceshift-ui/components/QuoteInvoice/LineItemTable.vue";
import Signatures from "serviceshift-ui/components/QuoteInvoice/Signatures.vue";
import Totals from "serviceshift-ui/components/QuoteInvoice/Totals.vue";
import TransactionDetails from "serviceshift-ui/components/QuoteInvoice/TransactionDetails.vue";
import EventBus from "serviceshift-ui/shared/src/lib/event_bus";
import { formatDate } from "serviceshift-ui/shared/src/mixins";
import { trackEvent } from "@/lib/pendo";

import SpbPastDueBanner from "@/Invoice/SpbPastDueBanner.vue";
import { Customizations } from "@/typings/customizations";
import { Invoice } from "@/typings/invoice";
export default {
  name: "Invoice",
  components: {
    LineItemTable,
    Totals,
    Signatures,
    InvoiceHeader,
    TransactionDetails,
    InvoiceNotes,
    InvoiceFooter,
    SpbPastDueBanner
  },
  emits: [
    "signature-click",
    "discount-click",
    "payments-click",
    "savings-click",
    "edit-transaction-note-click",
    "edit-transaction-reference-id-click",
    "equipment-request-click"
  ],
  props: {
    className: {
      type: String,
      default: ""
    },
    invoice: {
      type: Object as () => Invoice,
      default: null
    },
    customizations: {
      type: Object as () => Customizations,
      default: null
    },
    techMode: {
      type: Boolean,
      default: false
    },
    equipmentTypesEnabled: {
      type: Boolean,
      default: false
    },
    tenantTimezone: {
      type: String,
      default: ""
    }
  },
  mounted() {
    EventBus.$on("signature-click", this.signatureClick);
    EventBus.$on("discount-click", this.discountClick);
    EventBus.$on("payments-click", this.paymentsClick);
    EventBus.$on("savings-click", this.savingsClick);
    EventBus.$on("edit-transaction-note-click", this.editNotesClick);
    EventBus.$on(
      "edit-transaction-reference-id-click",
      this.editReferenceIdClick
    );
    EventBus.$on("equipment-request-click", this.equipmentRequestClick);

    // Adoption denominator for the SPB detail surface; lets product compare
    // banner-viewed rate against detail-viewed rate. Fires once per mount,
    // gated to invoices that actually carry an SPB payload.
    if (this.invoice?.progress_billing?.is_progress_billing) {
      trackEvent("spb_invoice_detail_viewed", {
        invoice_id: this.invoice.invoice_id,
        payment_status: this.invoice.progress_billing.payment_status
      });
    }
  },
  beforeUnmount() {
    EventBus.$off("signature-click", this.signatureClick);
    EventBus.$off("discount-click", this.discountClick);
    EventBus.$off("payments-click", this.paymentsClick);
    EventBus.$off("savings-click", this.savingsClick);
    EventBus.$off("edit-transaction-note-click", this.editNotesClick);
    EventBus.$off(
      "edit-transaction-reference-id-click",
      this.editReferenceIdClick
    );
    EventBus.$off("equipment-request-click", this.equipmentRequestClick);
  },
  methods: {
    formatDate,
    signatureClick(eventId) {
      if (eventId === this.invoice.invoice_id) {
        this.$emit("signature-click");
      }
    },
    discountClick(eventId) {
      if (eventId === this.invoice.invoice_id) {
        this.$emit("discount-click");
      }
    },
    paymentsClick(eventId) {
      if (eventId === this.invoice.invoice_id) {
        this.$emit("payments-click");
      }
    },
    savingsClick(eventId) {
      if (eventId === this.invoice.invoice_id) {
        this.$emit("savings-click");
      }
    },
    editNotesClick(transaction, eventId) {
      if (eventId === this.invoice.invoice_id) {
        this.$emit("edit-transaction-note-click", transaction);
      }
    },
    editReferenceIdClick(transaction, eventId) {
      if (eventId === this.invoice.invoice_id) {
        this.$emit("edit-transaction-reference-id-click", transaction);
      }
    },
    equipmentRequestClick(item, eventId) {
      if (eventId === this.invoice.invoice_id) {
        this.$emit("equipment-request-click", item, eventId);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
hr {
  border-color: var(--primary-light-accent-color) !important;
  margin: 0 !important;
  border: none !important;
  height: 1px !important;
  background-color: var(--primary-light-accent-color) !important;
}
</style>
